import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Check, Loader2, XCircle } from "lucide-react";
import { InlineWidget } from "react-calendly";

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/christian-goldlineadvisory/15min";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [tries, setTries] = useState(0);
  const sessionId = params.get("session_id");
  const isPlan = params.get("plan") === "installments";

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setTries(attempt);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (r.data.status === "expired") { setState("expired"); return; }
        if (attempt >= 7) { setState("error"); return; }
        setTimeout(() => poll(attempt + 1), 2500);
      } catch {
        if (attempt >= 7) { setState("error"); return; }
        setTimeout(() => poll(attempt + 1), 2500);
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ivory grain p-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-navy/10 p-10 text-center">
          <img src="/brand/logo.png" alt="Goldline" className="w-14 h-14 object-contain mx-auto mb-6" />
          {state === "polling" && (
            <>
              <Loader2 className="w-10 h-10 text-gold mx-auto animate-spin" />
              <h1 className="font-display text-3xl text-navy mt-6">Confirming your payment...</h1>
              <p className="text-navy/60 mt-3 text-sm">This takes a few seconds. (attempt {tries})</p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="w-16 h-16 bg-gold rounded-full mx-auto flex items-center justify-center">
                <Check className="w-10 h-10 text-navy" strokeWidth={2.5} />
              </div>
              <h1 className="font-display text-3xl text-navy mt-6">
                {isPlan ? "First payment received" : "Payment received"}
              </h1>
              <div className="w-12 h-[2px] bg-gold mx-auto mt-4" />
              <p className="text-navy/70 mt-5">
                Thank you — your full assessment is confirmed.{" "}
                {isPlan ? (
                  <>
                    The next 2 payments of <strong>$350</strong> will run automatically on the same day each month, then auto-stop.
                    {" "}<strong>Your branded 10-slide deck will be delivered within 2 business days.</strong>
                  </>
                ) : (
                  <><strong>Your branded 10-slide deck will be delivered within 2 business days.</strong></>
                )}{" "}
                Lock in your kickoff call below.
              </p>
            </>
          )}
          {(state === "expired" || state === "error") && (
            <>
              <XCircle className="w-14 h-14 text-navy/40 mx-auto" strokeWidth={1.5} />
              <h1 className="font-display text-3xl text-navy mt-6">
                {state === "expired" ? "Session expired" : "Hmm — we couldn't confirm"}
              </h1>
              <p className="text-navy/60 mt-3 text-sm">
                If you did pay, you'll get an email receipt shortly. Otherwise,
                <Link to="/free-assessment" className="text-gold hover:underline ml-1">try again</Link>.
              </p>
            </>
          )}
        </div>

        {state === "paid" && (
          <div className="bg-white border border-navy/10 -mt-px" data-testid="success-calendly">
            <div className="px-8 pt-8 pb-2 text-center">
              <div className="eyebrow text-gold">Step 2 — Schedule Kickoff</div>
              <h3 className="font-display text-2xl text-navy mt-2">Pick your kickoff slot.</h3>
              <p className="text-sm text-navy/60 mt-1">15 minutes. We confirm scope and start the 2-day sprint.</p>
            </div>
            <InlineWidget
              url={CALENDLY_URL}
              styles={{ height: "660px" }}
              pageSettings={{
                backgroundColor: "ffffff",
                primaryColor: "C69F56",
                textColor: "0E1A2B",
                hideEventTypeDetails: false,
                hideLandingPageDetails: false,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
